import React from "react";
import { Box, Typography, Button, CircularProgress } from "@mui/joy";
import {
  VisibilityIcon as Visibility,
  VisibilityOffIcon as VisibilityOff,
} from "../../styles/StyledMUI";
import { useLocation, useNavigate } from "react-router-dom";
import type { PortalCustomer } from "../../api/auth/Auth.typed";
import { useAlert } from "../../contexts/app/notificationToast";
import { useAuth } from "../../contexts/api";
import { useCompanyIdFromSearch } from "../../contexts/app/useCompanyIdFromSearch";
import { zekayStaticBranding } from "../../config/staticFiles";
import Cookies from "js-cookie";
import FooterBrand from "../common/FooterBrand";
import InvalidLinkCard from "../common/InvalidLinkCard";
import DarkInput from "../common/DarkInput";
import IconButtonCTA from "../common/IconButtonCTA";
import { authCardDarkInputSx, colorTokens, withAlpha } from "../../styles/globalStyles";

export default function LoginPage() {
  const [email, setEmail] = React.useState<string>("");
  const [password, setPassword] = React.useState<string>("");
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [showPassword, setShowPassword] = React.useState<boolean>(false);

  const { loginPortal } = useAuth();
  const { showAlert } = useAlert();
  const location = useLocation();
  const navigate = useNavigate();
  const zekayLogo = zekayStaticBranding.zekayIconPng();
  const companyId = useCompanyIdFromSearch();

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const companyIdParam = params.get("companyId");
    const creditId = params.get("creditId");
    if (companyIdParam) {
      Cookies.set("portalLoginParams", JSON.stringify({ companyId: companyIdParam, creditId }), {
        expires: 1,
        path: "/",
      });
    }
  }, [location.search]);

  const handleClickShowPassword = () => setShowPassword((prev) => !prev);

  const handleLogin = async () => {
    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();
    if (!trimmedEmail || !trimmedPassword) return;

    setIsLoading(true);
    try {
      const companyIdParam = new URLSearchParams(location.search).get("companyId");
      if (!companyIdParam) return;

      const credentials: PortalCustomer = {
        Email: trimmedEmail,
        Password: trimmedPassword,
      };
      const tokenResponse = await loginPortal(credentials, companyIdParam);
      if (tokenResponse) {
        navigate("/portal");
      }
    } catch (error) {
      if (error instanceof Error) showAlert(error.message, "danger");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await handleLogin();
  };

  if (!Number.isFinite(companyId) || companyId <= 0) {
    return (
      <InvalidLinkCard message="Ce lien est invalide, veuillez utiliser le lien de connexion reçu par email pour vous connecter." />
    );
  }

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        overflowX: "hidden",
        overflowY: "auto",
        padding: 2,
      }}
    >
      <Box
        sx={{
          flex: "1 1 auto",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 400,
            textAlign: "center",
            backgroundColor: "background.body",
            padding: 3,
            borderRadius: 2,
            boxShadow: `0 4px 12px ${withAlpha(colorTokens.bg.overlay, 0.1)}`,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", marginBottom: 3 }}>
            <img src={zekayLogo} width="84px" height="auto" alt="Logo Zekay" />
          </Box>

          <Typography level="h4" sx={{ marginBottom: 2 }}>
            Bienvenue !
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmitForm}
            noValidate
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
            }}
          >
            <DarkInput
              placeholder="Email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value.toLowerCase())}
              sx={authCardDarkInputSx}
            />

            <DarkInput
              placeholder="Mot de passe"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              endDecorator={
                <IconButtonCTA
                  type="button"
                  ctaVariant="neutral"
                  aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  onClick={handleClickShowPassword}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButtonCTA>
              }
              sx={authCardDarkInputSx}
            />

            <Button
              type="submit"
              variant="solid"
              color="primary"
              sx={{ marginTop: 2, width: "100%" }}
              disabled={isLoading || !email || !password}
            >
              {isLoading ? <CircularProgress size="sm" /> : "Se connecter"}
            </Button>
          </Box>
        </Box>
      </Box>
      <FooterBrand showChromeNote />
    </Box>
  );
}
